'use client';

import CopyIcon from '@mui/icons-material/ContentCopy';
import { Tooltip, TooltipProps, Typography, TypographyProps, alpha } from '@mui/material';
import tokens from '@verifime/design-tokens';
import { useMobile } from '@verifime/utils';
import React, { ReactNode, useState } from 'react';
import FlexBox from './FlexBox';

enum CopyState {
  NORMAL,
  HOVER,
  COPIED,
  FAILED,
}

export default function CopyableText({
  text,
  icon,
  notAvailableText = 'Not Available',
  textVariant = 'body2',
  textColor = 'textPrimary',
  tooltipPlacement = 'right',
}: Readonly<{
  text: ReactNode;
  icon?: JSX.Element;
  notAvailableText?: ReactNode;
  textColor?: TypographyProps['color'];
  textVariant?: TypographyProps['variant'];
  tooltipPlacement?: TooltipProps['placement'];
}>) {
  const isMobile = useMobile();
  const [copyState, setCopyState] = useState<CopyState>(
    isMobile ? CopyState.HOVER : CopyState.NORMAL,
  );

  return (
    <FlexBox
      onMouseOver={() => text && setCopyState(CopyState.HOVER)}
      onMouseOut={() => (isMobile ? setCopyState(CopyState.HOVER) : setCopyState(CopyState.NORMAL))}
      onClick={async () => {
        if (!text) return;
        try {
          // Clipboard is unavailable in insecure contexts; treat it as a failure
          // so the user still gets feedback instead of a dead click.
          if (!navigator.clipboard) throw new Error('clipboard unavailable');
          await navigator.clipboard.writeText(text.toString());
          setCopyState(CopyState.COPIED);
        } catch {
          // Write can reject (permission/focus/insecure context). Tell the user it
          // failed rather than showing nothing or a false "copied" confirmation.
          setCopyState(CopyState.FAILED);
        }
      }}
    >
      <FlexBox
        sx={{
          py: tokens.spacingSm,
          px: tokens.spacingXs,
          my: tokens.spacingXs,
          gap: tokens.spacingXs,
          borderRadius: tokens.borderRadiusSm,
          backgroundColor: (theme) =>
            copyState === CopyState.HOVER ? alpha(theme.palette.primary.main, 0.05) : 'none',
          '&:hover': {
            cursor: () => (copyState === CopyState.HOVER ? 'pointer' : 'default'),
          },
        }}
        alignItems="center"
      >
        {icon}
        <Tooltip
          title={copyState === CopyState.FAILED ? 'copy failed, please copy manually' : 'copied to clipboard'}
          open={copyState === CopyState.COPIED || copyState === CopyState.FAILED}
          placement={tooltipPlacement}
        >
          <Typography
            variant={textVariant}
            color={textColor}
            sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
          >
            {text || notAvailableText}
          </Typography>
        </Tooltip>
        {copyState === CopyState.HOVER && <CopyIcon fontSize="inherit" />}
      </FlexBox>
    </FlexBox>
  );
}
